import type { Ref } from 'vue'

/**
 * Async: Simply wait a number of milliseconds before resolving
 * @param ms length of time to wait (milliseconds)
 * @returns void
 */
export const wait = (ms: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve()
    }, ms)
  })
}

export interface TypewriterOptions {
  charDelay?: number // Delay between characters. It should also allow VueJS to do a render cycle
  stringPauseDelay?: number // Delay between different strings
  loopRestartDelay?: number // Delay at the end of a string list before looping again
}

/**
 * Creates a typewriter effect that cycles through an array of strings,
 * displaying them character-by-character in a reactive Vue reference
 *
 * @param strings - Array of strings to cycle through
 * @param displayText - Reactive ref that will be updated with the typing animation
 * @param options - Optional configuration for timing and delays
 * @returns Cleanup function to stop the animation
 * */
export function useTypewriterEffect(
  strings: string[],
  displayText: Ref<string>,
  options?: TypewriterOptions,
): { animate: () => void; stop: () => void } {
  let active = true

  async function animateOneRound() {
    displayText.value = ''
    const stringsToAnimate = [...strings]

    // Loop over each string in the array
    while (stringsToAnimate.length > 0 && active) {
      const nextString = stringsToAnimate.pop()
      if (nextString) {
        let charCounter = 1

        // Animate the characters in one string, stop when none remaining
        while (charCounter <= nextString.length) {
          const chars = nextString.substring(0, charCounter)
          displayText.value = chars
          await wait(options?.charDelay ?? 100) // Delay between characters. It should also allow VueJS to do a render cycle
          charCounter++
        }
      }
      await wait(options?.stringPauseDelay ?? 1000) // Delay between different strings
    }
  }

  async function animate() {
    while (active) await animateOneRound() // Keep animating unless stop() is called
    await wait(options?.loopRestartDelay ?? 2000) // Delay at the end of a string list before looping again
  }

  function stop() {
    active = false
  }

  return {
    animate,
    stop,
  }
}

export interface ParserOptions {
  boundaryPattern?: RegExp // Custom boundary delimiter pattern. default: {{...}}
  dataSeparator?: string // Character used to separate data items. defaults to: ','
  escapeCharacter?: string // Character used to escape the separator in data (default: '\\')
}

export interface ParsedCommand {
  command: string
  data: string[]
  index: number // Position in original string
  fullMatch: string
}

export interface ParsedAllDataBoundaries {
  textSegments: string[]
  commands: ParsedCommand[]
}

/**
 * Parse a data string into an array & also handle escaped separators
 *
 * @param dataString - The data string to parse
 * @param dataSeparator - Character used to separate data items
 * @param escapeCharacter - Character used to escape the separator
 * @returns Array of parsed data items
 */
function parseDataString(
  dataString: string,
  dataSeparator: string,
  escapeCharacter: string,
): string[] {
  const data: string[] = []
  let currentItem = ''
  let isEscaped = false

  for (let i = 0; i < dataString.length; i++) {
    const char = dataString[i]

    if (isEscaped) {
      currentItem += char
      isEscaped = false
    } else if (char === escapeCharacter) {
      isEscaped = true
    } else if (char === dataSeparator) {
      data.push(currentItem.trim())
      currentItem = ''
    } else {
      currentItem += char
    }
  }

  if (currentItem || dataString.endsWith(dataSeparator)) {
    data.push(currentItem.trim())
  }

  return data
}

/**
 * Parses and extracts all data boundaries from a string using the format {{command:data}}.
 * Returns an array of text segments and corresponding array of command objects.
 * There may be one more text segment than command
 *
 * @param input - The string to parse
 * @param options - Optional configuration for parsing behavior
 * @returns Object containing text segments and array of parsed commands
 *
 * @example
 * parseAllDataBoundaries("Hello {{typewriter:World,Friend}} and {{fade:Text}}!")
 * // Returns:
 * // {
 * //   textSegments: ["Hello ", " and ", "!"],
 * //   commands: [
 * //     { command: "typewriter", data: ["World", "Friend"], index: 6, fullMatch: "{{typewriter:World,Friend}}" },
 * //     { command: "fade", data: ["Text"], index: 37, fullMatch: "{{fade:Text}}" }
 * //   ]
 * // }
 */
export function parseAllDataBoundaries(
  input: string,
  options?: ParserOptions,
): ParsedAllDataBoundaries {
  const {
    boundaryPattern = /\{\{([^:}]+):([^}]+)\}\}/g,
    dataSeparator = ',',
    escapeCharacter = '\\',
  } = options || {}

  const commands: ParsedCommand[] = []
  const textSegments: string[] = []
  let lastIndex = 0

  let match
  while ((match = boundaryPattern.exec(input)) !== null) {
    const [fullMatch, command, dataString] = match

    // Skip if command or dataString is undefined
    if (!command || !dataString) {
      continue
    }

    // Add text before this command
    textSegments.push(input.substring(lastIndex, match.index))

    const data = parseDataString(dataString, dataSeparator, escapeCharacter)

    commands.push({
      command: command.trim(),
      data,
      index: match.index,
      fullMatch,
    })

    lastIndex = match.index + fullMatch.length
  }

  // Add remaining text after the last command
  textSegments.push(input.substring(lastIndex))

  return { textSegments, commands }
}
